import { HStack } from '@televet/kibble-ui/build/chakra';
import { Select, SelectOptionProps } from '@televet/kibble-ui/build/components/Select';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import React, { useMemo } from 'react';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ReportingPaymentsReportEvents } from 'shared/providers/TrackingProvider/EventNames';
import { CardBrand, CardFunding, ReportFilterTab, StripeDisputeStatus } from 'shared/types/graphql';
import { capitalize } from 'shared/utils';
import { StatusDescription } from '../records/status.records';
import {
  DisputeStatusFilter,
  PaymentReportQueriesVariables,
  TransactionCardBrandFilter,
  TransactionCardTypeFilter,
} from '../types';

interface ReportFiltersProps {
  queryVariables: PaymentReportQueriesVariables;
  setQueryVariables: React.Dispatch<React.SetStateAction<PaymentReportQueriesVariables>>;
  setResetPagination: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ReportFilters = ({
  queryVariables,
  setQueryVariables,
  setResetPagination,
}: ReportFiltersProps): JSX.Element => {
  const { trackEvent } = useTracking();

  const cardBrandOptions: SelectOptionProps[] = useMemo(() => {
    const cardBrands = Object.values(CardBrand);
    const options = cardBrands.map((cardBrand) => ({
      label: cardBrand,
      value: cardBrand,
      isSelected: queryVariables.cardBrandFiltering === cardBrand,
    }));
    return [{ label: 'All', value: 'All', isSelected: queryVariables.cardBrandFiltering === 'All' }, ...options];
  }, [queryVariables.cardBrandFiltering]);

  const cardTypeOptions: SelectOptionProps[] = useMemo(() => {
    const cardTypes = Object.values(CardFunding);
    const options = cardTypes.map((cardType) => ({
      label: capitalize(cardType),
      value: cardType,
      isSelected: queryVariables.cardTypeFiltering === cardType,
    }));
    return [{ label: 'All', value: 'All', isSelected: queryVariables.cardTypeFiltering === 'All' }, ...options];
  }, [queryVariables.cardTypeFiltering]);

  const disputeStatusOptions: SelectOptionProps[] = useMemo(() => {
    const disputeStatuses = Object.values(StripeDisputeStatus);
    const options = disputeStatuses.map((disputeStatus) => ({
      label: StatusDescription[disputeStatus],
      value: disputeStatus,
      isSelected: queryVariables.disputeStatusFiltering === disputeStatus,
    }));
    return [{ label: 'All', value: 'All', isSelected: queryVariables.disputeStatusFiltering === 'All' }, ...options];
  }, [queryVariables.disputeStatusFiltering]);

  return (
    <HStack className="PaymentReport__ReportFilters" my={2} mx={3} w="100%" spacing={4}>
      <Text size="sm">Filter Results by</Text>
      {queryVariables.selectedFilterTab !== ReportFilterTab.Disputes ? (
        <>
          <TextInput
            label="Client Name:"
            placeholder="Client Name"
            isClearable
            onClear={(): void => {
              setQueryVariables((curr) => ({ ...curr, skip: 0, nameFiltering: '' }));
              setResetPagination(true);
            }}
            isFieldInline
            size="sm"
            onFocus={(): void => {
              trackEvent(ReportingPaymentsReportEvents.FilterByTransactionName);
            }}
            type="text"
            value={queryVariables.nameFiltering}
            onChange={(e): void => {
              setQueryVariables((curr) => ({ ...curr, skip: 0, nameFiltering: e.target.value }));
              setResetPagination(true);
            }}
            minW="195px"
            formControlStyle={{ minW: '280px', maxW: '300px' }}
          />
          <Select
            label="Card Brand:"
            isFieldInline
            size="sm"
            value={queryVariables.cardBrandFiltering}
            options={cardBrandOptions}
            onFocus={(): void => {
              trackEvent(ReportingPaymentsReportEvents.FilterByTransactionCardBrand);
            }}
            listProps={{
              onSelect: (selectedItem): void => {
                const { value } = selectedItem;
                const val = value as TransactionCardBrandFilter;
                setQueryVariables((curr) => ({ ...curr, skip: 0, cardBrandFiltering: val }));
                setResetPagination(true);
              },
              maxH: '200px',
            }}
            formControlStyle={{ maxW: '300px' }}
            placeholder="Select Card Brand"
            minW="180px"
            sx={{
              '&.chakra-menu__menu-button': { minH: 8 },
              '.Select__Text': { fontSize: 'sm' },
            }}
          />
          <Select
            label="Card Type:"
            isFieldInline
            size="sm"
            value={queryVariables.cardTypeFiltering}
            options={cardTypeOptions}
            onFocus={(): void => {
              trackEvent(ReportingPaymentsReportEvents.FilterByTransactionCardType);
            }}
            listProps={{
              onSelect: (selectedItem): void => {
                const { value } = selectedItem;
                const val = value as TransactionCardTypeFilter;
                setQueryVariables((curr) => ({ ...curr, skip: 0, cardTypeFiltering: val }));
                setResetPagination(true);
              },
              maxH: '200px',
            }}
            formControlStyle={{ maxW: '300px' }}
            placeholder="Select Card Type"
            minW="180px"
            sx={{
              '&.chakra-menu__menu-button': { minH: 8 },
              '.Select__Text': { fontSize: 'sm' },
            }}
          />
        </>
      ) : (
        <Select
          label="Dispute Status:"
          isFieldInline
          size="sm"
          value={queryVariables.disputeStatusFiltering}
          options={disputeStatusOptions}
          onFocus={(): void => {
            trackEvent(ReportingPaymentsReportEvents.FilterByDisputeStatus);
          }}
          listProps={{
            onSelect: (selectedItem): void => {
              const { value } = selectedItem;
              const val = value as DisputeStatusFilter;
              setQueryVariables((curr) => ({ ...curr, skip: 0, disputeStatusFiltering: val }));
              setResetPagination(true);
            },
            maxH: '200px',
          }}
          formControlStyle={{ maxW: '350px' }}
          placeholder="Select Card Type"
          minW="180px"
          sx={{
            '&.chakra-menu__menu-button': { minH: 8 },
            '.Select__Text': { fontSize: 'sm' },
          }}
        />
      )}
    </HStack>
  );
};
